import React, { useContext } from "react";
import ErrorPopup from "../../../../components/element/ErrorPopup";
import Loader from "../../../../components/element/Loader";
import NoDataPlaceholder from "../../../../components/element/NoDataPlaceholder";
import { getPaymentFBOType, handleEmptyValue, formatToTwoDecimal } from "../../../../utils/utils";
import "./PaymentDueTable.css";
import dayjs from "dayjs";
import { DATE_FORMAT, INVOICE_STATUS } from "../../../../utils/constants";
import { StorageContext } from "../../../../services/StorageProvider";
import { Tooltip, IconButton } from "@mui/material";
import { InfoCircleOutlined } from "@ant-design/icons";

const PaymentDueTable = ({
  paymentDueData,
  indexStartFrom,
  isLoading,
  error,
  onSelectionChange,
  selectedInvoices = [],
}) => {
  const { fboListData } = useContext(StorageContext);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Add all current page items to existing selections (avoiding duplicates)
      const currentPageItems = paymentDueData?.content || [];
      const newSelections = [...selectedInvoices];

      currentPageItems.forEach((item) => {
        if (!newSelections.some((selected) => selected.id === item.id)) {
          newSelections.push(item);
        }
      });

      onSelectionChange(newSelections);
    } else {
      // Remove all current page items from selections
      const currentPageIds = (paymentDueData?.content || []).map(
        (item) => item.id
      );
      const filteredSelections = selectedInvoices.filter(
        (item) => !currentPageIds.includes(item.id)
      );

      onSelectionChange(filteredSelections);
    }
  };

  const handleSelectInvoice = (e, invoice) => {
    const updatedSelection = e.target.checked
      ? [...selectedInvoices, invoice]
      : selectedInvoices.filter((item) => item.id !== invoice.id);

    onSelectionChange(updatedSelection);
  };

  if (error) {
    if (error.status === 404) {
      return <NoDataPlaceholder />;
    } else {
      return <ErrorPopup children={error.message}></ErrorPopup>;
    }
  }

  if (isLoading) {
    return <Loader />;
  }

  if (paymentDueData?.content && paymentDueData?.content?.length === 0) {
    return <NoDataPlaceholder />;
  }

  return (
    <div className="payment-due-table-parent-container">
      <div className="payment-due-table-container">
        <table className="payment-due-table">
          <thead>
            <tr>
              <th className="width-90">
                <div className="checkbox-number-container">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={
                      paymentDueData?.content?.length > 0 &&
                      paymentDueData?.content?.every((item) =>
                        selectedInvoices.some(
                          (selected) => selected.id === item.id
                        )
                      )
                    }
                  />
                  No.
                </div>
              </th>
              <th>Invoice Number</th>
              <th>Invoice Date</th>
              <th>Currency</th>
              <th>Invoice Amount</th>
              <th>ABO Name</th>
              <th>Customer Number</th>
              <th>Fee Type</th>
              <th>Invoice Status</th>
            </tr>
          </thead>
          <tbody>
            {paymentDueData?.content?.map((item, index) => (
              <tr key={index}>
                <td>
                  <div className="checkbox-number-container">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.some(
                        (invoice) => invoice.id === item.id
                      )}
                      onChange={(e) => handleSelectInvoice(e, item)}
                    />
                    {indexStartFrom + (index + 1)}
                  </div>
                </td>
                <td>{handleEmptyValue(item?.dynamicsInvoiceNumber)}</td>
                <td>
                  {handleEmptyValue(
                    dayjs(item?.invoiceDate).utc().format(DATE_FORMAT)
                  )}
                </td>
                <td>BSD</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    BSD {handleEmptyValue(((item?.totalAmount || 0) - (item?.totalPaymentReceived || 0)).toFixed(2))}
                    {item?.totalPaymentReceived > 0 && (
                      <Tooltip
                        title={
                          <div style={{ fontSize: '11px', fontFamily: 'Inter, sans-serif' }}>
                            <div>Invoice Amount: BSD {formatToTwoDecimal(item.totalAmount)}</div>
                            <div>Paid Amount: BSD {formatToTwoDecimal(item.totalPaymentReceived)}</div>
                          </div>
                        }
                        arrow
                        placement="bottom"
                        classes={{ tooltip: 'partial-payment-tooltip' }}
                      >
                        <IconButton size="small" className="partial-payment-info-icon">
                          <InfoCircleOutlined />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </td>
                <td>
                  {
                    fboListData?.data?.find(
                      (fbo) => fbo.id === item?.fboCustomerId
                    )?.name
                  }
                </td>
                <td>{handleEmptyValue(item?.customerNumber)}</td>
                <td className="fee-type-cell">
                  {handleEmptyValue(getPaymentFBOType(item?.invoiceType))}
                </td>
                <td>
                  {item?.status ? INVOICE_STATUS[item?.status] : "---"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentDueTable;
